import React, { useEffect, useRef, useState } from 'react';
import BookmarkListItem from './BookmarkListItem';
import './Bookmarks.css';
import ConfirmModal from '../Common/ConfirmModal';
import { useConfirm } from '../Common/useConfirm';

// Approved folder-delete copy (Joel, 2026-10-21). One block, so a later edit stays here.
export const FOLDER_DELETE_COPY = {
  emptyTitle: 'Delete folder?',
  emptyConfirmLabel: 'Delete',
  emptyMessage: (name) => `Delete folder "${name}"? Its bookmarks will stay, outside any folder.`,
  title: (name) => `Delete "${name}"?`,
  line: (count) => (
    count === 1
      ? 'This folder has 1 bookmark. You can keep it or delete it with the folder.'
      : `This folder has ${count} bookmarks. You can keep them or delete them with the folder.`
  ),
  irreversible: "Deleted bookmarks can't be brought back.",
  safeLabel: 'Keep bookmarks',
  safeHint: "They'll stay, just outside any folder.",
  dangerLabel: (count) => (
    count === 1
      ? 'Delete folder and 1 bookmark'
      : `Delete folder and ${count} bookmarks`
  ),
  cancelLabel: 'Cancel',
};

// Folders do not nest. The count is the bookmarks in this folder.
export function folderBookmarkTotal(folder, bookmarks) {
  const listed = Array.isArray(bookmarks) ? bookmarks.length : 0;
  const reported = Number(folder?.bookmark_count);
  const counted = Number.isFinite(reported) && reported > 0 ? reported : 0;
  return Math.max(listed, counted);
}

/**
 * BookmarkFolderItem - Collapsible folder containing bookmarks
 */
function BookmarkFolderItem({
  folder,
  bookmarks = [],
  isExpanded,
  onToggleCollapse,
  onRename,
  onDelete,
  onBookmarkDelete,
  onBookmarkCategoryChange,
  onBookmarkTitleChange,
  onMoveBookmarkToRoot,
  folders = [],
  onMoveToFolder,
  onReorder,
  onReorderFolder,
  canMoveUp = false,
  canMoveDown = false,
  bookmarkPlacement,
}) {
  const [isRenaming, setIsRenaming] = useState(false);
  const [renamingValue, setRenamingValue] = useState(folder.name);
  const [showMenu, setShowMenu] = useState(false);
  const renameOnce = useRef(false);
  const menuButtonRef = useRef(null);
  const { confirm, choose, modalProps } = useConfirm();

  // Same close as the bookmark ⋯ menu. Escape must not reach the header,
  // which would collapse or expand the folder.
  useEffect(() => {
    if (!showMenu) return undefined;
    const onKey = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      setShowMenu(false);
      menuButtonRef.current?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [showMenu]);

  const handleToggleCollapse = (e) => {
    if (e?.target?.closest?.('.bookmark-folder-menu-container')) return;
    e?.stopPropagation();
    onToggleCollapse(folder.id);
  };

  const handleStartRename = (e) => {
    e?.stopPropagation();
    renameOnce.current = false;
    setIsRenaming(true);
    setRenamingValue(folder.name);
    setShowMenu(false);
  };

  // Close the name box before the save returns. Enter unmounts the box,
  // which also blurs it, so the second call must not save again.
  const commitRename = () => {
    if (renameOnce.current) return;
    renameOnce.current = true;
    const trimmedValue = renamingValue.trim();
    setIsRenaming(false);
    if (trimmedValue && trimmedValue !== folder.name) {
      onRename?.(folder.id, trimmedValue);
    } else {
      setRenamingValue(folder.name);
    }
  };

  const cancelRename = () => {
    renameOnce.current = true;
    setRenamingValue(folder.name);
    setIsRenaming(false);
  };

  const handleRenameKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      commitRename();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      cancelRename();
    }
  };

  const handleDelete = async (event) => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    setShowMenu(false);
    const count = folderBookmarkTotal(folder, bookmarks);
    if (count === 0) {
      const ok = await confirm({
        title: FOLDER_DELETE_COPY.emptyTitle,
        message: FOLDER_DELETE_COPY.emptyMessage(folder.name),
        confirmLabel: FOLDER_DELETE_COPY.emptyConfirmLabel,
        destructive: true,
      });
      if (ok) onDelete?.(folder.id);
      return;
    }
    const choice = await choose({
      title: FOLDER_DELETE_COPY.title(folder.name),
      message: FOLDER_DELETE_COPY.line(count),
      detail: FOLDER_DELETE_COPY.irreversible,
      confirmLabel: FOLDER_DELETE_COPY.safeLabel,
      confirmNote: FOLDER_DELETE_COPY.safeHint,
      alternateLabel: FOLDER_DELETE_COPY.dangerLabel(count),
      cancelLabel: FOLDER_DELETE_COPY.cancelLabel,
    });
    if (choice === 'safe') onDelete?.(folder.id);
    if (choice === 'danger') onDelete?.(folder.id, { withBookmarks: true });
  };

  const handleFolderReorder = (event, direction) => {
    event.stopPropagation();
    if (direction === 'up' && !canMoveUp) return;
    if (direction === 'down' && !canMoveDown) return;
    setShowMenu(false);
    onReorderFolder?.(folder.id, direction);
  };

  const handleMenuToggle = (e) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
  };

  const handleDoubleClick = (e) => {
    e.stopPropagation();
    handleStartRename();
  };

  return (
    <div className={`bookmark-folder-item${showMenu ? ' bookmark-folder-item--menu-open' : ''}`}>
      <div className="bookmark-folder-header" onClick={handleToggleCollapse}>
        <button
          className={`bookmark-folder-toggle ${isExpanded ? 'bookmark-folder-toggle--expanded' : ''}`}
          aria-label={isExpanded ? 'Collapse folder' : 'Expand folder'}
        >
          ▸
        </button>

        <span className="bookmark-folder-icon">
          {isExpanded ? '📂' : '📁'}
        </span>

        {isRenaming ? (
          <input
            type="text"
            className="bookmark-folder-name-input"
            value={renamingValue}
            onChange={(e) => setRenamingValue(e.target.value)}
            onKeyDown={handleRenameKeyDown}
            onBlur={commitRename}
            onClick={(e) => e.stopPropagation()}
            autoFocus
          />
        ) : (
          <span
            className="bookmark-folder-name"
            onDoubleClick={handleDoubleClick}
            title="Double-click to rename"
          >
            {folder.name}
          </span>
        )}

        <span className="bookmark-folder-count" title={`${folder.bookmark_count || bookmarks.length} bookmarks`}>
          {folder.bookmark_count || bookmarks.length}
        </span>

        <div
          className="bookmark-folder-menu-container"
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            ref={menuButtonRef}
            className="bookmark-folder-menu-button"
            onClick={handleMenuToggle}
            aria-label="Folder options"
            aria-haspopup="menu"
            aria-expanded={showMenu}
          >
            ⋯
          </button>

          {showMenu && (
            <>
              <div
                className="bookmark-folder-menu-backdrop"
                onClick={(event) => {
                  event.stopPropagation();
                  setShowMenu(false);
                }}
                onMouseDown={(event) => event.stopPropagation()}
              />
              <div
                className="bookmark-folder-menu"
                role="menu"
                aria-label="Folder options"
                onClick={(event) => event.stopPropagation()}
                onMouseDown={(event) => event.stopPropagation()}
              >
                <button
                  type="button"
                  role="menuitem"
                  className="bookmark-folder-menu-item"
                  onClick={(event) => handleFolderReorder(event, 'up')}
                  disabled={!canMoveUp}
                >
                  Move up
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="bookmark-folder-menu-item"
                  onClick={(event) => handleFolderReorder(event, 'down')}
                  disabled={!canMoveDown}
                >
                  Move down
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="bookmark-folder-menu-item"
                  onClick={handleStartRename}
                >
                  Rename
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="bookmark-folder-menu-item bookmark-folder-menu-item--danger"
                  onClick={handleDelete}
                >
                  Delete
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {isExpanded && (
        <div className="bookmark-folder-contents">
          {bookmarks.length === 0 ? (
            <div className="bookmark-folder-empty">(empty)</div>
          ) : (
            bookmarks.map((bookmark) => {
              const place = bookmarkPlacement?.(bookmark.id) || { index: 0, count: 1 };
              return (
              <div key={bookmark.id} className="bookmark-folder-bookmark">
                <BookmarkListItem
                  bookmark={{
                    ...bookmark,
                    user_title: bookmark.name,
                    bookmark_type: bookmark.bookmark_type
                  }}
                  onDelete={() => onBookmarkDelete?.(bookmark.id)}
                  onCategoryChange={(id, cat) => onBookmarkCategoryChange?.(id, cat)}
                  onTitleChange={(id, title) => onBookmarkTitleChange?.(id, title)}
                  folders={folders}
                  currentFolderId={folder.id}
                  onMoveToFolder={onMoveToFolder}
                  onReorder={onReorder}
                  canMoveUp={place.index > 0}
                  canMoveDown={place.index < place.count - 1}
                />
                <button
                  className="bookmark-move-to-root"
                  onClick={() => onMoveBookmarkToRoot?.(bookmark.id)}
                  title="Move to root level"
                >
                  ↑
                </button>
              </div>
              );
            })
          )}
        </div>
      )}
      <ConfirmModal {...modalProps} />
    </div>
  );
}

export default BookmarkFolderItem;
